import { Controller } from "@hotwired/stimulus"

// An inbox card (release_watch/inbox/_card): ticking the figures you've
// curated that its listings go into (with none ticked, it's a new figure). One ticked is the one merged into;
// more than one, and the others are folded into the one kept ("Keep this
// one" picks it). Merge goes into the kept one, showing its options for
// each listing; every picture's pop-up shows the card's listings beside
// the ticked figures (a curated one's, beside it and the others ticked).
// A figure found by the card's search ticks in the same way: its row moves
// into the merge box while ticked (back to the results when not), and how
// the listings would go into it joins the box. Each other ticked figure, folded
// in, gets a row in the kept one's box for the option it goes in as. The
// pencil (or the maker's tag) opens the name and maker for editing.
// Merge is green for a strong match (a row's data-strength), and a line
// above it says where each listing goes, each place's box in its colour.
export default class extends Controller {
  static targets = ["row", "tick", "map", "fold", "bar", "picked", "rest", "restHead", "restList", "merge", "mergeVerb", "mergeLabel", "peerLink", "matchLink", "sum", "warn"]
  static values = { peek: String, members: String, count: Number }

  connect() {
    this.kept = this.rowTargets.find(row => this.tick(row).checked) || null
    this.ready = true
    this.draw()
  }

  // Where a row goes back to unticked: the list below the box, or the search results it came from.
  rowTargetConnected(row) {
    if (this.rowTargets.some(other => other !== row && other.dataset.id === row.dataset.id)) return row.remove()
    if (!row.home) row.home = row.parentElement === (this.hasPickedTarget && this.pickedTarget) ? this.restListTarget : row.parentElement
    if (this.ready) this.draw()
  }

  // A searched figure's map: into the box, beside the others (one per figure).
  mapTargetConnected(map) {
    if (!this.hasBarTarget || map.parentElement === this.barTarget) return
    if (this.mapTargets.some(other => other !== map && other.dataset.for === map.dataset.for)) return map.remove()
    this.barTarget.insertBefore(map, this.hasMergeTarget ? this.mergeTarget : null)
  }

  // Where a figure's folded-in row goes back to when it isn't folded in: its own box.
  foldTargetConnected(fold) {
    if (!fold.home) fold.home = fold.parentElement
  }

  keep(event) {
    event.preventDefault()
    this.kept = event.target.closest("[data-rw-inbox-card-target='row']")
    this.draw()
  }

  edit() {
    this.element.classList.add("editing")
    this.element.querySelector(".ib-edit input[type=text]")?.focus()
  }

  cancel() {
    this.element.classList.remove("editing")
    this.element.querySelector(".ib-edit")?.reset()
  }

  draw() {
    const rows = [...this.rowTargets].sort((a, b) => a.dataset.index - b.dataset.index)
    const ticked = rows.filter(row => this.tick(row).checked)
    if (!ticked.includes(this.kept)) this.kept = ticked[0] || null
    rows.forEach(row => {
      row.classList.toggle("on", ticked.includes(row))
      row.classList.toggle("is-kept", row === this.kept)
    })
    // The ticked ones inside the merge box, the rest where they came from, each in the matcher's order.
    if (this.hasPickedTarget) {
      this.pickedTarget.append(...ticked)
      rows.filter(row => !ticked.includes(row)).forEach(row => row.home?.append(row))
      this.restTarget.hidden = !this.restListTarget.children.length
      this.restHeadTarget.textContent = ticked.length ? "Also looks like" : "Looks like"
    }
    if (this.hasBarTarget) this.barTarget.classList.toggle("many", ticked.length > 1)
    // Only the kept one's fields are sent; of a page split or not, only the way shown (rw-page-options).
    this.mapTargets.forEach(map => {
      const on = !!this.kept && map.dataset.for === this.kept.dataset.id
      map.hidden = !on
      map.querySelectorAll("select, input").forEach(field => {
        const split = field.closest("[data-split]")
        field.disabled = !on || (!!field.dataset.when && field.dataset.when !== split?.dataset.split) || this.unpicked(field)
      })
    })
    // Each other ticked figure, folded in: its row in the kept one's box, its option typed or picked from the kept one's.
    const keptMap = this.kept && this.mapTargets.find(map => map.dataset.for === this.kept.dataset.id)
    this.foldTargets.forEach(fold => {
      const on = !!keptMap && fold.dataset.for !== this.kept.dataset.id && ticked.some(row => row.dataset.id === fold.dataset.for)
      if (on) keptMap.querySelector(".ib-map-rows").append(fold)
      else if (fold.parentElement !== fold.home) fold.home?.append(fold)
      fold.hidden = !on
      const select = fold.querySelector("select")
      if (on && select) this.fillOptions(select, keptMap)
      fold.querySelectorAll("select, input").forEach(field => { field.disabled = !on || this.unpicked(field) })
    })
    this.links(ticked)
    // None ticked: the box names each listing's option for a new figure, and Watch it / Catalog only lead again.
    this.element.classList.toggle("merging", !!this.kept)
    if (!this.hasBarTarget) return
    this.barTarget.classList.toggle("newfig", !this.kept)
    if (!this.kept || !this.hasMergeTarget) return

    const name = this.kept.dataset.name
    const others = ticked.filter(row => row !== this.kept).map(row => row.dataset.name)
    this.mergeTarget.value = this.kept.dataset.id
    // Green only for a match that says it's this figure; only the maker in common, and a word of caution.
    const strength = this.kept.dataset.strength || "strong"
    this.mergeTarget.classList.toggle("green", strength === "strong")
    this.mergeTarget.classList.toggle("quiet", strength !== "strong")
    if (this.hasWarnTarget) this.warnTarget.hidden = strength !== "weak"
    this.count()
    this.mergeVerbTarget.textContent = others.length ? "Merge & fold" : "Merge"
    // A figure a line, names lined up: those folded, the last ending "into", then the kept one ("into" first, alone).
    const into = () => Object.assign(document.createElement("em"), { textContent: "into" })
    const line = (...parts) => {
      const row = Object.assign(document.createElement("span"), { className: "ib-btn-row" })
      row.append(...parts)
      return row
    }
    const lines = others.length
      ? [...others.map((other, i) => (i === others.length - 1 ? line(other, " ", into()) : line(other))), line(name)]
      : [line(into(), " ", name)]
    this.mergeLabelTarget.replaceChildren(...lines)
    this.mergeTarget.dataset.turboConfirm = `Put ${this.countValue === 1 ? "this" : `these ${this.countValue}`} into ${name}, each under the option picked` +
      (others.length ? `, and fold ${others.join(" and ")} into it too?` : "?")
  }

  // Where merging puts each listing (each enabled option box in the kept one's map, a folded figure's too), by place:
  // "3 into Frost Claw: 2 as Deluxe, 1 as a new option Red · 1 into Ember Fang · 1 skipped". Each box is outlined in
  // its place's colour: the kept one sky, each other figure its own, a new option dashed, a skipped one red.
  count() {
    if (!this.hasSumTarget) return
    const map = this.kept && this.mapTargets.find(m => m.dataset.for === this.kept.dataset.id)
    if (!map) { this.sumTarget.replaceChildren(); return }
    const keptName = this.kept.dataset.name
    const known = new Set([...map.querySelectorAll("datalist option")].map(o => o.value))
    const colours = ["#fb923c", "#f472b6", "#2dd4bf"]
    const places = new Map([[keptName, { colour: "#38bdf8", n: 0, how: new Map() }]])
    let skipped = 0
    map.querySelectorAll("select:not(:disabled)").forEach(select => {
      if (!select.classList.contains("wx-option-select") && !select.classList.contains("po-where")) return
      const v = select.value
      let place = keptName, how = null, fresh = false
      if (v === "__figure__") return
      if (v === "__skip__") { skipped++; select.style.borderColor = "#f87171"; select.style.borderStyle = "dashed"; return }
      if (v.startsWith("figure:")) place = select.selectedOptions[0].text.replace(/^Another figure: /, "")
      else if (v === "__new__") { how = select.parentElement.querySelector("[data-new-option]")?.value.trim() || null; fresh = !!how }
      else if (select.classList.contains("po-where")) how = select.closest(".po-line")?.querySelector("input.wx-option-select")?.value.trim() || null
      else if (!v && select.dataset.when === "one" && select.name.startsWith("option[")) how = "all its options"
      else { how = v || null; fresh = !!v && !known.has(v) }
      if (!places.has(place)) places.set(place, { colour: colours[(places.size - 1) % colours.length], n: 0, how: new Map() })
      const at = places.get(place)
      at.n++
      if (place === keptName) {
        const said = how ? (fresh ? `as a new option ${how}` : `as ${how}`) : "with no option"
        at.how.set(said, (at.how.get(said) || 0) + 1)
      }
      select.style.borderColor = at.colour
      select.style.borderStyle = fresh ? "dashed" : ""
    })
    const parts = [...places].filter(([, at]) => at.n).map(([place, at]) => {
      const span = document.createElement("span")
      const to = Object.assign(document.createElement("span"), { className: "to", textContent: place })
      to.style.color = at.colour
      span.append(`${at.n} into `, to)
      if (at.how.size) {
        const how = [...at.how].map(([said, n]) => at.how.size > 1 || n > 1 ? `${n} ${said}` : said).join(", ")
        span.append(Object.assign(document.createElement("span"), { className: "how", textContent: `: ${how}` }))
      }
      return span
    })
    if (skipped) parts.push(Object.assign(document.createElement("span"), { textContent: `${skipped} skipped` }))
    this.sumTarget.replaceChildren(...parts.flatMap((part, i) => (i ? [" · ", part] : [part])))
  }

  // An option dropdown's "New option…": its box shows, for the name (sent after the dropdown's value); any other hides it.
  pickOption(event) {
    const select = event.target
    const box = select.parentElement.querySelector("[data-new-option]")
    if (!box) return
    box.hidden = select.value !== "__new__"
    box.disabled = box.hidden || select.disabled
    if (!box.hidden) box.focus()
  }

  // A new option's box, hidden until "New option…" is picked: never sent.
  unpicked(field) {
    return field.hasAttribute("data-new-option") && field.hidden
  }

  // A folded-in figure's dropdown: the kept one's options (its box's list), keeping the one picked.
  fillOptions(select, map) {
    const names = [...map.querySelectorAll("datalist option")].map(option => option.value)
    const picked = select.value
    select.querySelector("optgroup.ib-kept-options")?.remove()
    // Its own name guessed as a new option, unless the kept one has it already.
    select.querySelectorAll("optgroup option").forEach(option => { option.hidden = names.includes(option.value) })
    if (!names.length) return
    const group = Object.assign(document.createElement("optgroup"), { label: "Its options", className: "ib-kept-options" })
    group.append(...names.map(name => new Option(name, name, false, name === picked)))
    select.prepend(group)
    if (names.includes(picked)) select.value = picked
  }

  // The pop-up's figures: the listings on top, then the ticked ones (a
  // curated figure's own picture puts it first among them).
  links(ticked) {
    const keys = ticked.map(row => row.dataset.key)
    this.peerLinkTargets.forEach(link => { link.href = this.peekPath(keys) })
    this.matchLinkTargets.forEach(link => {
      const own = link.closest("[data-rw-inbox-card-target='row']").dataset.key
      link.href = this.peekPath([own, ...keys.filter(key => key !== own)])
    })
  }

  peekPath(keys) {
    const all = [...this.membersValue.split(","), ...keys]
    return `${this.peekValue}?keys=${encodeURIComponent(all.join(","))}&alike=1`
  }

  tick(row) {
    return row.querySelector("[data-rw-inbox-card-target='tick']")
  }
}
